import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { unregisterWebPush } from '@/services/push/webPush';
import { authApi } from '@/services/api/auth';
import { NotificationBell } from './NotificationBell';
import { Icon, type IconName } from './ui/icons';
import { dashboardPathFor } from '@/domain/navigation';

export function MainLayout({ children }: { children: React.ReactNode }) {
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    // Tear down push + grab the token, then log out via the backend (revokes the
    // session AND removes this browser's push token from the DB). Best-effort —
    // never block logout. clearAuth runs afterwards (it wipes the JWT).
    try {
      const token = await unregisterWebPush();
      await authApi.logout(token ?? undefined);
    } catch {
      // ignore — fall through to local cleanup
    }
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };

  const isAdmin = user?.role === 'RADIUS_ADMIN';
  const caps = user?.partner?.capabilities;

  const dashboardPath = dashboardPathFor();

  type NavItem = { id: string; label: string; icon: IconName; path: string };

  // Tabs follow what the partner is allowed to do, not what it is called. A
  // partner granted both gets both; a new partner gets whatever an admin ticks,
  // with no code change here.
  const navItems: NavItem[] = isAdmin
    ? [
        { id: 'overview', label: 'Overview', icon: 'Home', path: '/admin/dashboard' },
        { id: 'referrals', label: 'All Referrals', icon: 'List', path: '/admin/referrals' },
        { id: 'trials', label: 'All Trials', icon: 'Trial', path: '/admin/trials' },
        { id: 'partners', label: 'Partners', icon: 'Users', path: '/admin/partners' },
        { id: 'sales-reps', label: 'Sales Reps', icon: 'UserSingle', path: '/admin/sales-reps' },
        // Commission is hidden while the rate moves onto each partner (Step 3).
        // The screen still sets the fallback amount used by partners that have
        // no rate of their own, so the route is deliberately left reachable at
        // /admin/commission rather than removed.
        // { id: 'commission', label: 'Commission', icon: 'Coin', path: '/admin/commission' },
        { id: 'configurations', label: 'Configurations', icon: 'Settings', path: '/admin/configurations' },
      ]
    : user?.partner
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: 'Home', path: dashboardPath },
        ...(caps?.canViewReferrals
          ? [{ id: 'referrals', label: 'My Referrals', icon: 'List', path: '/referrals' } as NavItem]
          : []),
        ...(caps?.canViewTrials
          ? [{ id: 'trials', label: 'My Trials', icon: 'Trial', path: '/trials' } as NavItem]
          : []),
      ]
    : [];

  const isNavActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return '';
    return name.split(' ').map((s) => s[0]).join('').toUpperCase();
  };

  const getUserRole = () => {
    if (user?.role === 'RADIUS_ADMIN') return 'Radius Admin';
    return user?.partner?.type ? `${user.partner.type} Rep` : 'Sales Representative';
  };

  return (
    <div className="app">
      {/* Mobile sidebar backdrop */}
      {isMobileNavOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsMobileNavOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobileNavOpen ? 'sidebar-open' : ''}`}>
        <div className="brand">
          <img
            src="/radius-logo.png"
            alt="Radius XR"
            className="brand-logo"
            style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <div className="brand-name">Referral</div>
            <div className="brand-sub">Tracking App</div>
          </div>
        </div>

        {/* Workspace Nav Group */}
        <div className="nav-group">
          <div className="nav-label">Workspace</div>
          {navItems.map((item) => {
            const isActive = isNavActive(item.path);
            const NavIcon = Icon[item.icon];
            return (
              <button
                key={item.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <NavIcon className="nav-icon" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Account Nav Group */}
        <div className="nav-group">
          <div className="nav-label">Account</div>
          <button
            className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
            onClick={() => navigate('/profile')}
          >
            <Icon.UserCircle className="nav-icon" />
            <span>Profile</span>
          </button>
          <button className="nav-item" onClick={handleLogout}>
            <Icon.SignOut className="nav-icon" />
            <span>Sign out</span>
          </button>
        </div>

        {/* User Card */}
        <div className="user-card">
          <div className="avatar" style={{
            width: '44px',
            height: '44px',
            borderRadius: '11px',
            background: 'linear-gradient(135deg, #6c4cff 0%, #ff6b8a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
            fontSize: '16px',
            flexShrink: 0
          }}>
            {getInitials(user?.fullName)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="user-name">{user?.fullName}</div>
            <div className="user-role">{getUserRole()}</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Topbar */}
        <header className="topbar">
          <button
            className="hamburger-btn"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Open menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div style={{ flex: 1 }}></div>
          {/* Notifications are a sales-rep feature (Glaukos / Topcon). */}
          {user?.role === 'SALES_REP' && <NotificationBell />}
        </header>

        {/* Content Area */}
        <main className="content" style={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
